import React, { useState, useEffect } from 'react';

import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";

// Services

import { getDropDownOptions } from '../../../store/options/options.actions';
import { handleSetFormValues, handleSetShowModuleIntro, handleSetModule, handleSetFormStep } from '../../../store/forms/forms.actions';

// Hooks

import InsertClientpartialData from '../../../hooks/InsertClientpartialData';

// Components

import FormWrapper from '../../UI/FormWrapper/FormWrapper';
import FormStepper from '../../UI/FormStepper/FormStepper';
import Loader from '../../UI/Loader/Loader';
import Modal from '../../UI/Modal/Modal';
import EditNameAndDocumentForm from '../EditNameAndDocumentForm/EditNameAndDocumentForm';

// Modules

import FormModuleIntro from './FormModuleIntro/FormModuleIntro';
import PersonalInfo from './1.PersonalInfo/PersonalInfo';
import AdditionalInfo from './2.AdditionalInfo/AdditionalInfo';
import JobInfo from './3.JobInfo/JobInfo';
import ProductInfo from './4.ProductInfo/ProductInfo';
import RegisterClient from './5.RegisterClient/RegisterClient';

// Validation

import { FormInitialValues } from './FormInitialValues';

// Data

import { FORM_MODULES } from './FormModules';

// Styles

import './Styles.scss';

export default function UserDetailsForm() {
  const { state } = useLocation();

  const { insertPartialDataIsLoading, insertPartialDataError, insertPartialData, handleInsertPartialData } = InsertClientpartialData();
  const dropDownOptions = useSelector((state) => state.options.dropDownOptions);

  const showModuleIntro = useSelector((state) => state.forms.showModuleIntro);
  const formValues = useSelector((state) => state.forms.values);
  const module = useSelector((state) => state.forms.module);
  const formStep = useSelector((state) => state.forms.formStep);

  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [sendData, setSendData] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();


  // Setup form with formik

  const formik = useFormik({
    initialValues: state?.userPartialData?.form_data || formValues || FormInitialValues,
    enableReinitialize: true,
    validationSchema: FORM_MODULES[module]?.schema[formStep],
    onSubmit: (values, actions) => {
      sendFormRequest(values, actions);
    },
  });

  useEffect(() => {
    // dispatch(getDropDownOptions());
    const clientEmail = localStorage.getItem("clientEmail");

    if (clientEmail && state?.userPartialData?.selected_product) {
      dispatch(getDropDownOptions());
    } else {
      navigate(`/`);
    }
  }, [dispatch])

  useEffect(() => {
    if (state?.userPartialData?.selected_product) {
      formik.setFieldValue('selectedProducts', state?.userPartialData?.products);
      formik.setFieldValue('cliente.nombre', state?.userPartialData?.nombre);
      formik.setFieldValue('cliente.primerApellido', state?.userPartialData?.primerApellido);
      formik.setFieldValue('cliente.tipoDocumento', state?.userPartialData?.tipoDocumento);
      formik.setFieldValue('cliente.mail', state?.userPartialData?.email);

      formik.setFieldValue('cliente.fechaNacimiento', state?.userPartialData?.fechaNacimiento);
      formik.setFieldValue('cliente.fechaExpedicionIdentificacion', state?.userPartialData?.fechaExpedicionIdentificacion);
      formik.setFieldValue('cliente.fechaVenceIdentificacion', state?.userPartialData?.fechaVenceIdentificacion);
      formik.setFieldValue('cliente.nacionalidad', state?.userPartialData?.nacionalidad);
      formik.setFieldValue('cliente.tipoCliente', state?.userPartialData?.tipoCliente);

      if (state?.userPartialData?.cedulaRif) { formik.setFieldValue('cliente.cedulaRif', state?.userPartialData?.cedulaRif) }
      if (state?.userPartialData?.pasaporte) { formik.setFieldValue('cliente.pasaporte', state?.userPartialData?.pasaporte) }
      if (state?.userPartialData?.segundoApellido) { formik.setFieldValue('cliente.segundoApellido', state?.userPartialData?.segundoApellido) }
      if (state?.userPartialData?.estadoCivil) { formik.setFieldValue('cliente.estadoCivil', state?.userPartialData?.estadoCivil); }

      dispatch(handleSetFormValues(formik.values));
    }
  }, [dispatch])


  // Set form module and step from partial data

  useEffect(() => {
    if (state?.userPartialData?.form_data) {
      dispatch(handleSetShowModuleIntro(false));
      dispatch(handleSetModule(state?.userPartialData?.module));
      dispatch(handleSetFormStep(state?.userPartialData?.step));
    }
  }, [dispatch, state?.userPartialData?.form_data])


  // Send Form Request

  const sendFormRequest = async (values, actions) => {
    const currentModule = FORM_MODULES[module];

    if (!(module === 3 && formStep > 0)) {
      const formVals = {
        ...values,
        recaudos: {
          ...FormInitialValues?.recaudos
        }
      };

      handleInsertClientPartialData(formVals, module, formStep);
      dispatch(handleSetFormValues(formVals));
    }

    if ((formStep + 1 === currentModule?.steps) && (module + 1 === FORM_MODULES.length)) {

      setSendData(true);

    } else if ((formStep + 1 === currentModule?.steps) && (module + 1 !== FORM_MODULES.length)) {

      dispatch(handleSetShowModuleIntro(true));
      dispatch(handleSetModule(module + 1));
      dispatch(handleSetFormStep(0));

    } else if (formStep + 1 !== currentModule?.steps) {

      if (values.hasBankReference === 'si' && (module === 1 && formStep === 1)) {
        dispatch(handleSetFormStep(formStep + 2));
      } else {
        dispatch(handleSetFormStep(formStep + 1));
      }

      actions.setTouched({});
      actions.setSubmitting(false);

    }

    // Scroll to top on form step change 

    window?.scrollTo({ top: 0, behavior: "smooth" });
  }


  // Go to the previous form module

  const goToPreviousModule = (moduleNumber) => {
    const previousModuleLength = FORM_MODULES[moduleNumber]?.steps;

    dispatch(handleSetModule(moduleNumber));
    dispatch(handleSetFormStep(previousModuleLength - 1));

    // Scroll to top on form step change 

    window?.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Handle Insert Client Partial Data on step change

  const handleInsertClientPartialData = (formValues, currentModule, currentStep) => {
    const data = {
      ...state?.userPartialData,
      module: currentModule,
      step: currentStep,
      data: {
        ...formValues
      },
    }
    handleInsertPartialData(data);
  };

  useEffect(() => {
    const formVals = {
      ...formik.values,
      recaudos: { ...FormInitialValues?.recaudos }
    }
    if (module === 3 && formStep > 1) {
      // console.log('wont save', module, formStep)
    } else {
      handleInsertClientPartialData(formVals, module, formStep);
      dispatch(handleSetFormValues(formVals));
    }
  }, [dispatch, module, formStep])

  return (
    <>
      {(dropDownOptions.isLoading || dropDownOptions.error) &&
        <Loader
          message={'Cargando...'}
          error={dropDownOptions?.error}
          action={() => dispatch(getDropDownOptions())}
        />}

      {(showModuleIntro && !dropDownOptions?.isLoading && !dropDownOptions?.error) &&
        <FormModuleIntro
          formik={formik}
          modules={FORM_MODULES}
          currentModule={module}
          handleSetShowModuleIntro={handleSetShowModuleIntro}
          clientAge={formik.values?.cliente?.fechaNacimiento}
          goToPreviousModule={goToPreviousModule}
        />}

      {(!showModuleIntro && !dropDownOptions?.isLoading && !dropDownOptions?.error) &&
        <FormWrapper>

          <FormStepper
            modules={FORM_MODULES}
            currentModule={module}
            currentStep={formStep + 1}
            setModule={handleSetModule}
            setFormStep={handleSetFormStep}
          />

          <form className='form-container' onSubmit={formik.handleSubmit}>

            {/* 1. Personal Info */}

            {module === 0 && !sendData &&
              <PersonalInfo
                formik={formik}
                formStep={formStep}
                setFormStep={handleSetFormStep}
                setShowEditNameModal={setShowEditNameModal}
              />}

            {/* 2. Additional Info */}

            {module === 1 && !sendData &&
              <AdditionalInfo
                formik={formik}
                formStep={formStep}
                setModule={handleSetModule}
                setFormStep={handleSetFormStep}
                goToPreviousModule={goToPreviousModule}
              />}

            {/* 3. Job Info */}

            {module === 2 && !sendData &&
              <JobInfo
                formik={formik}
                formStep={formStep}
                setFormStep={handleSetFormStep}
                goToPreviousModule={goToPreviousModule}
              />}

            {/* 4. Product Info */}

            {module === 3 && !sendData &&
              <ProductInfo
                formik={formik}
                formStep={formStep}
                setModule={handleSetModule}
                setFormStep={handleSetFormStep}
                goToPreviousModule={goToPreviousModule}
              />}

            {/* 5. Register Client */}

            {sendData &&
              <RegisterClient
                formik={formik}
                formStep={formStep}
                setModule={handleSetModule}
                setFormStep={handleSetFormStep}
                setSendData={setSendData}
                goToPreviousModule={goToPreviousModule}
              />}

          </form>

        </FormWrapper>}


      {/* Edit Names and Document ID Modal */}

      <Modal show={showEditNameModal} title={'Editar nombres y documento'}>
        <EditNameAndDocumentForm
          setShowEditNameModal={setShowEditNameModal}
          form={formik}
          state={state}
          formInitialValues={FormInitialValues}
        />
      </Modal>
    </>
  )
}